import { ColliStateName } from '@frontend/colli/types';
import { ChevronDown, ChevronUp, ICONS, Package } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { SlotBadge } from '@frontend/slot/badge';
import { UserBadge } from '@frontend/user/badge';
import { LuCheck, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import usePickUpOverview from './pick-up-overview.controller';

const ID = 'pick-up-overview';
const completedStates = [ColliStateName.COLLI_COMPLETED, ColliStateName.COLLI_COMPLETED_WITH_FAILURES];

const PickUpOverview = () => {
    const viewProps = usePickUpOverview();

    return (
        <WizardScreenContainer
            id={ID}
            header={{ title: CommonMessage.BUTTONS.PICK_UP }}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled
            }}
            containerClassName='flex flex-1 flex-col items-start justify-start gap-3 self-stretch px-5 pt-6'>
            <div className='flex w-full flex-col items-start justify-start gap-3'>
                <div className='text-foreground-default justify-start text-base font-medium'>
                    {CommonMessage.DETAIL.DETAILS_OF(CommonMessage.OBJECTS.TRANSACTION.DEFAULT)}
                </div>
                <div className='flex w-full flex-col items-start justify-start gap-3'>
                    {viewProps.transaction && (
                        <div className='bg-background-surface inline-flex w-full flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.SUPPLIER.DEFAULT}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.supplier ?? '/'}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>
                                    <FormattedMessage
                                        id='pick-up-code'
                                        defaultMessage='Pick-up code'
                                    />
                                </div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.pin_code?.code}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.tracking_number}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.purchase_order}</div>
                            </div>
                        </div>
                    )}
                    <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div className='flex items-center justify-start gap-4'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    {viewProps.collis ? (
                                        <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    ) : (
                                        <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    )}
                                    <Package className='size-1/2' />
                                </div>

                                <div className='inline-flex flex-col items-start justify-center gap-1'>
                                    <div className='text-foreground-default justify-start text-base font-semibold'>{CommonMessage.OBJECTS.PARCEL.PLURAL}</div>
                                    <div className='text-brand-muted justify-start text-base font-medium'>
                                        {viewProps.collis?.length ?? 0} {CommonMessage.OBJECTS.TRANSACTION.ITEMS}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-col gap-2 self-stretch'>
                            {viewProps.collis &&
                                viewProps.collis.map((colli) => {
                                    const isAlreadyCompleted = completedStates.includes(colli.state);

                                    return (
                                        <div
                                            className={`text-foreground-default justify-start self-stretch text-base font-medium${isAlreadyCompleted ? 'opacity-60' : ''}`}
                                            key={colli.id}>
                                            <span className='flex flex-row justify-between'>
                                                <span className='flex flex-col gap-1'>
                                                    {CommonMessage.OBJECTS.PARCEL.DEFAULT} ({colli.type})
                                                    <span className='text-xs text-stone-500'>{colli.label}</span>
                                                    {isAlreadyCompleted && (
                                                        <span className='text-status-warning text-sm font-medium'>
                                                            <FormattedMessage
                                                                id='mobile.wizard.pick-up-scan-parcels.already-picked-up'
                                                                defaultMessage='Already picked up'
                                                                description='Label shown on a parcel that is already in a completed state'
                                                            />
                                                        </span>
                                                    )}
                                                </span>
                                                {!isAlreadyCompleted && colli.current_slot_id && (
                                                    <span className='bg-border-default text-foreground-default inline-flex h-6 items-center justify-start gap-2.5 self-stretch rounded px-1 text-sm font-medium'>
                                                        {CommonMessage.OBJECTS.SLOT.DEFAULT}:{' '}
                                                        <SlotBadge
                                                            id={colli.current_slot_id}
                                                            className='ms-0 ps-0'
                                                        />
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>

                    {viewProps.receivers && (
                        <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                            <div className='inline-flex items-center justify-between self-stretch'>
                                <div className='flex items-center justify-start gap-4'>
                                    <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                        {viewProps.receivers ? (
                                            <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                        ) : (
                                            <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                        )}
                                        <ICONS.ENTITY.USER className='size-1/2' />
                                    </div>

                                    <div className='inline-flex flex-col items-start justify-center gap-1'>
                                        <div className='text-foreground-default justify-start text-base font-semibold'>
                                            {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}
                                        </div>
                                        {viewProps.receivers.map((receiver) => (
                                            <div
                                                key={receiver.id}
                                                className='text-brand-muted justify-start text-base font-medium'>
                                                <UserBadge
                                                    id={receiver.user_id}
                                                    className='ms-0 ps-0'
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {viewProps.transaction?.shipping_notes && (
                        <>
                            <div className='text-foreground-default justify-start text-base font-medium'>
                                {CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}
                            </div>
                            <div
                                data-state='Open'
                                className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                                <div className='inline-flex items-center justify-between self-stretch'>
                                    <div className='flex items-center justify-start gap-4'>{viewProps.transaction.shipping_notes}</div>
                                </div>
                            </div>
                        </>
                    )}

                    {viewProps.transaction?.comments && (
                        <>
                            <div className='inline-flex items-center justify-between self-stretch'>
                                <div className='text-foreground-default justify-start text-center text-base font-medium'>
                                    {CommonMessage.OBJECTS.TRANSACTION.COMMENTS}
                                </div>
                                <div
                                    className='inline-flex h-5 w-5 flex-col items-center justify-center gap-2.5'
                                    onClick={() => viewProps.setCommentsVisible(!viewProps.commentsVisible)}>
                                    {viewProps.commentsVisible ? <ChevronUp /> : <ChevronDown />}
                                </div>
                            </div>
                            {viewProps.commentsVisible && (
                                <div
                                    data-state='Open'
                                    className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                                    <div className='inline-flex items-center justify-between self-stretch'>
                                        <div className='flex items-center justify-start gap-4'>{viewProps.transaction.comments}</div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </WizardScreenContainer>
    );
};

export default PickUpOverview;
